import { useTranslation } from "react-i18next";
import React, { useEffect, useRef } from "react";
import * as echarts from "echarts";
import { formatPercent, formatMonthDay } from "@/utils/format";
import { calculatePercentageYAxis } from "@/utils/chartHelpers";
import {
  usePlatformVisibility,
  PlatformVisibilitySeries,
} from "@/hooks/usePlatformVisibility";
import type { BrandAiTrendPureDict } from "@/services/api/brandAiMetricsTrend";

interface PlatformVisibilityChartProps {
  // 可直接传入通用序列
  xLabels?: string[];
  series?: PlatformVisibilitySeries[];
  selectedKeys?: string[];
  // 支持从 hook 传入数据（字典结构）
  brandAiMetricsTrend?: BrandAiTrendPureDict;
  brandId?: string;
  brandName?: string;
  categoryId?: string;
  days?: number;
  metricType?: string;
  currentMetric?: {
    key: string;
    title: string;
    tooltip: string;
  };
  onBrandRefresh?: (
    brands: Array<{
      brandId: string;
      brandName: string;
      color: string;
      isCurrentBrand: boolean;
    }>
  ) => void;
  brands?: Array<{
    brandId: string;
    brandName: string;
    color: string;
    isCurrentBrand: boolean;
  }>;
}

export const PlatformVisibilityChart: React.FC<
  PlatformVisibilityChartProps
> = ({
  xLabels,
  series,
  brandAiMetricsTrend,
  metricType = "mentionRate",
  onBrandRefresh,
  brands,
}) => {
  const { t } = useTranslation();
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstRef = useRef<echarts.ECharts | null>(null);

  // 使用平台可见性 Hook
  const shouldUseVisibilityHook =
    !!brandAiMetricsTrend && Object.keys(brandAiMetricsTrend).length > 0;

  const visibilityResult = usePlatformVisibility({
    brandAiMetricsTrend,
    metricType,
  });

  // 获取最终数据 - 优先使用 hook 数据，如果没有则回退到传入的 series，最后使用默认数据
  const finalSeries =
    shouldUseVisibilityHook && visibilityResult.chartData?.series?.length
      ? visibilityResult.chartData.series
      : series;
  // 工具：校验是否是 YYYY-MM-DD 日期标签
  const isValidDateLabel = (v: any) =>
    typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v);
  const areValidDateLabels = (arr: any) =>
    Array.isArray(arr) && arr.length > 0 && arr.every(isValidDateLabel);

  // 兜底：如果 hook 尚未返回且传入了 brandAiMetricsTrend（字典），从所有模型的日期取并集、排序
  const fallbackXLabelsFromDict = React.useMemo(() => {
    if (!brandAiMetricsTrend || typeof brandAiMetricsTrend !== "object")
      return undefined;
    const metricKeys = Object.keys(brandAiMetricsTrend).filter(
      (k) => k !== "__proto__" && k !== "__aiNameMap"
    );
    if (!metricKeys.length) return undefined;
    const allDays = new Set<string>();
    for (const mk of metricKeys) {
      const byAi = (brandAiMetricsTrend as any)[mk] as Record<
        string,
        Array<{ day: string; rate: number }>
      >;
      if (!byAi || typeof byAi !== "object") continue;
      Object.values(byAi).forEach((seriesArr) => {
        if (Array.isArray(seriesArr)) {
          seriesArr.forEach((p) => {
            if (isValidDateLabel(p?.day)) allDays.add(p.day);
          });
        }
      });
    }
    const labels = Array.from(allDays);
    labels.sort();
    return labels.length ? labels : undefined;
  }, [brandAiMetricsTrend]);
  // 选择最稳妥的 x 轴：优先用 hook 的且是有效日期；否则用字典推导；否则用传入 xLabels 且有效；否则保持空待默认
  const hookX = visibilityResult.chartData?.xLabels;
  const finalXLabels = areValidDateLabels(hookX)
    ? hookX
    : fallbackXLabelsFromDict ||
      (areValidDateLabels(xLabels) ? xLabels : undefined);

  // 兜底修正：若 xLabels 看起来像是模型ID（例如 UUID），用日期序列替代
  const looksLikeIds =
    Array.isArray(finalXLabels) &&
    finalXLabels.length > 0 &&
    finalXLabels.every((v) => {
      return typeof v === "string" && /[0-9a-f]{4,}-[0-9a-f-]{8,}/i.test(v);
    });
  const finalPlatforms =
    shouldUseVisibilityHook && visibilityResult.chartData?.platforms?.length
      ? visibilityResult.chartData.platforms
      : [];

  // 为 AI 平台生成品牌格式的数据（兼容现有接口）
  const finalBrands =
    brands ||
    (finalPlatforms
      ? finalPlatforms.map((platform, index) => ({
          brandId: `platform_${platform}`,
          brandName: platform,
          color: ["#1890ff", "#52c41a", "#faad14", "#f5222d", "#722ed1"][
            index % 5
          ],
          isCurrentBrand: false,
        }))
      : undefined);

  // 自动调用品牌刷新回调
  useEffect(() => {
    if (onBrandRefresh && finalBrands && finalBrands.length > 0) {
      onBrandRefresh(finalBrands);
    }
  }, [finalBrands, onBrandRefresh]);

  // 清洗外部传入的 series，避免 undefined 造成图例或系列报错
  const safeSeries = Array.isArray(finalSeries)
    ? finalSeries
        .filter(
          (s): s is PlatformVisibilitySeries =>
            !!s && typeof s.name === "string" && Array.isArray(s.values)
        )
        .map((s) => ({
          ...s,
          values: s.values.map((v) => (Number.isFinite(v) ? v : 0)),
        }))
    : undefined;

  // 生成时间轴标签：确保从左到右显示从老到新的时间
  const computedXLabels = (() => {
    if (finalXLabels && finalXLabels.length && !looksLikeIds) {
      // 直接使用传入的日期标签，保持原始顺序（假设数据已经按时间顺序排列）
      return finalXLabels;
    }
    if (fallbackXLabelsFromDict && fallbackXLabelsFromDict.length) {
      return fallbackXLabelsFromDict;
    }
    if (xLabels && xLabels.length) return xLabels;

    // 默认标签：从左到右显示从老到新的时间（1/10, 2/10, 3/10, ..., 7/10）
    const labels: string[] = [];
    for (let i = 1; i <= 7; i += 1) {
      labels.push(`${i}/10`);
    }
    return labels;
  })();

  const chartOption = {
    legend: {
      type: "plain",
      orient: "horizontal",
      bottom: 0,
      left: "center",
      itemWidth: 24,
      itemHeight: 24,
      itemGap: 20,
      textStyle: {
        fontSize: 12,
        color: "#6b7280",
      },
      align: "auto",
    },
    tooltip: {
      trigger: "axis",
      formatter: (params: any[]) => {
        const axis =
          params?.[0]?.axisValueLabel || params?.[0]?.axisValue || "";
        const formattedAxis = axis ? formatMonthDay(axis) : "";
        const head = formattedAxis
          ? `<div style="margin-bottom:4px;font-weight:600;">${formattedAxis}</div>`
          : "";
        const body = (params || [])
          .map((p: any) => {
            const val =
              typeof p?.value === "number" ? p.value : Number(p?.value);
            const text = Number.isFinite(val) ? formatPercent(val, 1) : "--%";
            return `${p.marker} ${p.seriesName}: ${text}`;
          })
          .join("<br/>");
        return head + body;
      },
      axisPointer: {
        type: "line",
        snap: true,
        lineStyle: { type: "dashed", width: 2, color: "#6b5bd2" },
        z: 30,
      },
      backgroundColor: "rgba(255,255,255,0.95)",
      borderColor: "#e5e7eb",
      textStyle: { color: "#0f172a" },
    },

    grid: {
      left: 5,
      right: 20,
      bottom: 80,
      containLabel: true,
    },
    xAxis: {
      type: "category",
      boundaryGap: false,
      data: computedXLabels,
      axisLine: {
        show: true,
        lineStyle: { color: "#d1d5db" },
      },
      axisLabel: {
        color: "#6b7280",
        fontSize: 12,
        formatter: (value: string) => {
          // 使用公共函数格式化日期
          try {
            return formatMonthDay(value);
          } catch {
            return value;
          }
        },
      },
      splitLine: {
        show: true,
        lineStyle: { type: "dashed", color: "#e5e7eb" },
      },
    },
    yAxis: (() => {
      const { min, max } = calculatePercentageYAxis(safeSeries || []);
      return {
        type: "value",
        min,
        max,
        axisLabel: { formatter: (val: number) => formatPercent(val, 1) },
        splitLine: {
          show: true,
          lineStyle: { type: "dashed", color: "#e5e7eb" },
        },
      };
    })(),
    series: (() => {
      if (safeSeries && safeSeries.length > 0) {
        return safeSeries.map((s, index) => {
          // 优化的颜色配置 - 与 BrandComparisonChart 保持一致
          const optimizedColors = [
            "#1890ff", // 蓝色
            "#52c41a", // 绿色
            "#ff4d4f", // 红色
            "#fa8c16", // 橙色
            "#722ed1", // 紫色
            "#13c2c2", // 青色
            "#eb2f96", // 粉色
            "#f5222d", // 深红
            "#fadb14", // 黄色
            "#2f54eb", // 深蓝
          ];
          const color =
            s.color || optimizedColors[index % optimizedColors.length];
          return {
            name: s.name,
            type: "line",
            smooth: true,
            data: s.values,
            itemStyle: { color },
            lineStyle: { color, width: 2, cap: "round" },
            symbol: "circle",
            symbolSize: 6,
            emphasis: {
              focus: "series",
              itemStyle: { shadowBlur: 6, shadowColor: color },
            },
            areaStyle: {
              color: new (echarts as any).graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: echarts.color.modifyAlpha(color, 0.15) },
                { offset: 1, color: echarts.color.modifyAlpha(color, 0.02) },
              ]),
            },
          };
        });
      }

      // 如果没有数据，返回空数组而不是错误的空数组map操作
      return [];
    })(),
  };
  // 初始化实例
  useEffect(() => {
    if (chartRef.current && !chartInstRef.current) {
      chartInstRef.current = echarts.init(chartRef.current);
      const handleResize = () => chartInstRef.current?.resize();
      window.addEventListener("resize", handleResize);
      return () => {
        window.removeEventListener("resize", handleResize);
        chartInstRef.current?.dispose();
        chartInstRef.current = null;
      };
    }
  }, []);

  // 更新配置
  useEffect(() => {
    if (chartInstRef.current) {
      chartInstRef.current.setOption(chartOption, true);
    }
  }, [chartOption, finalSeries, finalXLabels, safeSeries, computedXLabels]);

  return (
    <div style={{ width: "100%", border: "none" }}>
      {/* 图表容器 - 参考情绪趋势样式 */}
      <div
        style={{
          position: "relative",
          width: "100%",
          height: 430,
          background: "#ffffff",
          borderRadius: 6,
          border: "1px solid #e5e7eb",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          padding: "2px",
        }}
      >
        <div
          ref={chartRef}
          style={{ width: "100%", height: "410px", border: "none" }}
        />

        {shouldUseVisibilityHook && visibilityResult.loading && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255, 255, 255, 0.8)",
              color: "#6b7280",
            }}
          >
            {t("search_detail.competitor_loading")}
          </div>
        )}
      </div>
    </div>
  );
};

export default PlatformVisibilityChart;
